import {
  DashboardComponentNames,
  DianaDataType,
  EntityDataType,
  getAppliedFilters,
  IDashboardComponentProperties,
  IDashboardDataGridProperties,
  IFlow,
  ILayout,
  IProperty,
  IWork,
  makeDynamicReportingPageStore,
  mergeFiltersValues,
  prepareWorksForWorkGrid,
  reportingPageDefaultColumns,
  SortModel,
  urlWithHost,
  useAbortRequestWhenFiltersValuesChanged,
  useDefaultDashboardDataSource,
  useEnvironment,
  useHeaderHeightForScrollableContent,
  usePerformanceGraphQLParameters,
  usePerformancePageStore,
  useServerAggregatedColumnOperation,
  WorkGrid,
} from '@diana/core';
import { GridApi, GridSortModel } from '@mui/x-data-grid-premium';
import { forwardRef, useCallback } from 'react';
import { routeUrls } from '../../app/Routing/RouteUrls';

interface IReportingTableProps {
  reportId: string;
  layout: ILayout<
    IDashboardComponentProperties | IDashboardDataGridProperties,
    DashboardComponentNames
  >;
  flows: IFlow[];
  forceEmptySelection: boolean;
  disableLastVisited?: boolean;
  supportedProperties: IProperty[];
  onSortModelChange: (model: GridSortModel) => void;
  sortModel: SortModel;
}

export const ReportingTable = forwardRef<GridApi, IReportingTableProps>((props, ref) => {
  const {
    flows,
    forceEmptySelection,
    supportedProperties,
    disableLastVisited = false,
    reportId,
    layout,
    onSortModelChange,
    sortModel,
  } = props;
  const useReportingPageStore = makeDynamicReportingPageStore(reportId);
  const availableFilters = useReportingPageStore((store) => store.availableFilters);
  const filtersValues = useReportingPageStore((store) => store.filtersValues);
  const defaultFiltersValues = useReportingPageStore((store) => store.defaultFiltersValues);
  const appliedFilters = getAppliedFilters({ filtersValues });
  const allFilterValues = mergeFiltersValues({
    selectedFiltersValues: filtersValues,
    defaultFiltersValues,
  });

  const { abort } = useAbortRequestWhenFiltersValuesChanged({
    filtersValues: allFilterValues,
    sortModel,
  });

  const queryParameters = usePerformanceGraphQLParameters({
    flows,
    filters: availableFilters,
    filtersValues: allFilterValues,
    reportId,
    abort,
    sortModel,
    showCompleted: true,
    showDeleted: false,
    showInProgress: true,
    isExpandedFeatureEnabled: true, // should be true for all performance reports/dashboards
  });

  const {
    isFetching,
    data: works,
    fetchNextPage,
  } = useDefaultDashboardDataSource({
    reportId,
    queryParameters,
    flows,
    sortModel,
  });

  const aggregateColumnOperation = useServerAggregatedColumnOperation({
    filters: availableFilters,
    filtersValues: allFilterValues,
    properties: supportedProperties,
    dianaDataType: DianaDataType.Work,
    flows,
    showCompleted: queryParameters.showCompleted,
    showDeleted: queryParameters.showDeleted,
    showInProgress: queryParameters.showInProgress,
    dateFrom: queryParameters.dateFrom,
    dateTo: queryParameters.dateTo,
    isExpandedFeatureEnabled: queryParameters.isExpandedFeatureEnabled, // should be true for all performance reports/dashboards
  });

  const headerHeight = useHeaderHeightForScrollableContent();
  const selectedColumns = supportedProperties.map((c) => ({
    ...c,
    sortable: !!c.valuePath,
  }));

  const reports = usePerformancePageStore((store) => store.reports);
  const columnsData = reports.find((r) => r.id === reportId)?.columnsData;

  const environment = useEnvironment();
  const createWorkLink = useCallback(
    (work: IWork) => urlWithHost(routeUrls.work.url({ workId: work.id, environment }, {})),
    [environment],
  );

  const preparedWorks = prepareWorksForWorkGrid({
    works,
    properties: supportedProperties,
  });

  return (
    <WorkGrid
      ref={ref}
      isManageColumnsPinned
      storageKey={`${reportId}-reporting-grid`}
      rows={preparedWorks}
      columns={selectedColumns}
      forceEmptySelection={forceEmptySelection}
      defaultColumnsOrder={
        columnsData?.orderedFields && columnsData.orderedFields.length > 0
          ? [...columnsData.orderedFields]
          : reportingPageDefaultColumns
      }
      defaultColumnVisibilityModel={
        columnsData?.columnVisibilityModel &&
        Object.keys(columnsData.columnVisibilityModel).length > 0
          ? { ...columnsData.columnVisibilityModel }
          : undefined
      }
      createWorkLink={createWorkLink}
      dataType={EntityDataType.Work}
      disableLastVisited={disableLastVisited}
      filters={layout.restrictFilters ? undefined : appliedFilters}
      aggregateColumnOperation={aggregateColumnOperation}
      gridProps={{
        // rowId always exists here because it is being used for expanded work items - https://dev.azure.com/rise-x/DIANA%20Ecosystem%20Services/_workitems/edit/8655
        idKey: 'rowId',
        height: `calc(100vh - ${headerHeight}px)`,
        autoHeight: false,
        sortingMode: 'server',
        onSortModelChange,
        loading: isFetching,
        onRowsScrollEnd: () => {
          fetchNextPage();
        },
      }}
    />
  );
});
